import { AxiosError } from 'axios'
import { ok, err, Result } from 'neverthrow'
import { AIAssistanceAPIPayloadType, AIAssistanceAPIResponseType } from 'Types/AIAssistance.types'
import { s as rougeS, n as rougeN } from 'js-rouge'
import { ExpertsClient } from './axios-clients/experts.client'
import { CustomLiveFeedbackResponseType } from 'Types/CustomTask.types'
import { ContainerDataType, ContainerType, LinearConversationsDataType } from 'Types/Tasks.types'

export type PlagiarismCheckResponseType = {
    total_words: number
    words_matched: number
    plag_percent: number
    source_count: number
    text_matched: string[]
    sources: {
        url: string
        words_matched: number
        plag_percent: number
        text_matched: string[]
    }[]
}

export type PlagiarismRequestType = {
    text: string
    taskPluginId: string
}

export type AIDetectionResponseType = {
    ai_score: number
    total_ai_words: number
    feedback: string
    probable_ai_texts: string[] | null
    total_words: number
}

export type AIDetectionRequestType = {
    text: string
    taskPluginId: string
}

type BaseLiveFeedbackRequestType = {
    taskPluginId: string
    userId: string
    jobId: string
    refId: string
}

export type LiveFeedbackRequestType = BaseLiveFeedbackRequestType & {
    inputContainer?: ContainerDataType<
        ContainerType.LinearConversation,
        LinearConversationsDataType
    >
    containerData?: ContainerDataType<ContainerType, unknown>
}

export type LiveFeedbackResponseType = {
    response: {
        QC: {
            [key: string]: {
                prediction: 'Yes' | 'No' | number
                reasoning: string
                title: string
                qc_param_version_id: string
            }
        }
    }
    status: string
    requested_by: {
        rubric_version_id: string
        workflow_id: string
    }
}

export type LiveFeedbackForCustomTaskRequestType = {
    taskPluginId: string
    domain: string
    question: string
    humanAnswer: string
    modelResponse: string
}

export const checkForPlagiarismApi = async (
    requestPayload: PlagiarismRequestType
): Promise<Result<PlagiarismCheckResponseType, AxiosError>> => {
    try {
        const { text, taskPluginId } = requestPayload
        const resp = await ExpertsClient.post<PlagiarismCheckResponseType>(
            'v1/plugin-management/plagiarism-checker',
            {
                text: text,
                plugin_task_association_id: taskPluginId,
            }
        )
        return ok(resp.data)
    } catch (e) {
        return err(e as AxiosError)
    }
}

export const checkForAIContentApi = async (
    requestPayload: AIDetectionRequestType
): Promise<Result<AIDetectionResponseType, AxiosError>> => {
    try {
        const { text, taskPluginId } = requestPayload
        const resp = await ExpertsClient.post<AIDetectionResponseType>(
            'v1/plugin-management/ai-detection',
            {
                text: text,
                plugin_task_association_id: taskPluginId,
            }
        )
        return ok(resp.data)
    } catch (e) {
        return err(e as AxiosError)
    }
}

export const getAIAssistanceResponseApi = async (
    payload: AIAssistanceAPIPayloadType
): Promise<Result<AIAssistanceAPIResponseType, AxiosError>> => {
    try {
        const { taskId, contextId, taskPluginId, content } = payload
        const resp = await ExpertsClient.post<AIAssistanceAPIResponseType>(
            'v1/plugin-management/ai-assistance',
            {
                task_id: taskId,
                context_id: contextId,
                plugin_task_association_id: taskPluginId,
                content: content,
            }
        )
        return ok(resp.data)
    } catch (e) {
        return err(e as AxiosError)
    }
}

export type GetSentimentApiResponseType = {
    sentiment: string
}

export const getSentiment = async (
    text: string
): Promise<Result<GetSentimentApiResponseType, AxiosError>> => {
    try {
        const payload = {
            text: text,
        }
        const resp = await ExpertsClient.post<GetSentimentApiResponseType>(
            `v1/plugin-management/get-sentiment`,
            payload
        )
        return ok(resp.data)
    } catch (e) {
        return err(e as AxiosError)
    }
}

export const getSummarisationQualityScore = (
    reference_text: string,
    final_text: string,
    model: string
): number => {
    switch (model) {
        case 'ROUGE-S':
            return rougeS(final_text, reference_text, { beta: 0.75 })
        case 'ROUGE-N':
            return rougeN(final_text, reference_text, { n: 2 })
    }
    return 0
}

export const getLiveFeedbackWithAIApi = async (
    requestPayload: LiveFeedbackRequestType
): Promise<Result<LiveFeedbackResponseType, AxiosError>> => {
    try {
        const { taskPluginId, userId, jobId, refId, containerData, inputContainer } = requestPayload
        const resp = await ExpertsClient.post<LiveFeedbackResponseType>(
            'v1/plugin-management/live-feedback',
            {
                plugin_task_association_id: taskPluginId,
                request: {
                    annotator_id: userId,
                    job_id: jobId,
                    ref_id: refId,
                    input_container: inputContainer,
                    container_data: containerData,
                },
            }
        )
        return ok(resp.data)
    } catch (e) {
        return err(e as AxiosError)
    }
}

export const getLiveFeedbackWithAIForCustomTaskApi = async (
    requestPayload: LiveFeedbackForCustomTaskRequestType
): Promise<Result<CustomLiveFeedbackResponseType, AxiosError>> => {
    try {
        const { taskPluginId, modelResponse, humanAnswer, domain, question } = requestPayload
        const resp = await ExpertsClient.post('v1/plugin-management/live-feedback', {
            plugin_task_association_id: taskPluginId,
            request: {
                domain,
                question,
                human_answer: humanAnswer,
                llama_response: modelResponse,
            },
        })
        return ok(resp.data)
    } catch (e) {
        return err(e as AxiosError)
    }
}
